import { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Copy, Check, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { mediaApi } from '@/lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMedia() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    try {
      const response = await mediaApi.getAll();
      setMedia(response.data);
    } catch (error) {
      console.error('Error loading media:', error);
      toast.error('Fehler beim Laden der Medien');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const response = await mediaApi.upload(file);
        setMedia(prev => [response.data, ...prev]);
      }
      toast.success(`${files.length} Datei(en) erfolgreich hochgeladen`);
    } catch (error) {
      toast.error('Fehler beim Hochladen der Dateien');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await mediaApi.delete(deleteId);
      setMedia(media.filter(m => m.id !== deleteId));
      toast.success('Datei erfolgreich gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen der Datei');
    } finally {
      setDeleteId(null);
    }
  };

  const copyUrl = (item) => {
    const fullUrl = `${API_URL}${item.url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(item.id);
    toast.success('URL in Zwischenablage kopiert');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="admin-media">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-light">Medienbibliothek</h1>
          <p className="text-[#666666] mt-1">Hochgeladene Bilder verwalten</p>
        </div>
        <div>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            className="hidden"
            id="media-upload"
            ref={fileInputRef}
            data-testid="media-upload-input"
          />
          <label
            htmlFor="media-upload"
            className="btn-primary inline-flex items-center gap-2 cursor-pointer"
          >
            {uploading ? (
              <div className="spinner" />
            ) : (
              <>
                <Upload size={16} />
                Bilder hochladen
              </>
            )}
          </label>
        </div>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="aspect-square bg-[#E5E5E5] animate-pulse" />
          ))}
        </div>
      ) : media.length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] p-12 text-center">
          <ImageIcon size={48} className="mx-auto text-[#E5E5E5] mb-4" />
          <p className="text-[#666666] mb-4">Noch keine Bilder hochgeladen</p>
          <label
            htmlFor="media-upload"
            className="btn-secondary inline-flex items-center gap-2 cursor-pointer"
          >
            <Upload size={16} />
            Erstes Bild hochladen
          </label>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {media.map((item) => (
            <div
              key={item.id}
              className="group relative bg-white border border-[#E5E5E5] overflow-hidden"
              data-testid={`media-item-${item.id}`}
            >
              {/* Image */}
              <div className="aspect-square">
                <img
                  src={`${API_URL}${item.url}`}
                  alt={item.original_name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
                {/* Actions */}
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => copyUrl(item)}
                    className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gold transition-colors"
                    title="URL kopieren"
                    data-testid={`copy-url-${item.id}`}
                  >
                    {copiedId === item.id ? (
                      <Check size={14} className="text-green-600" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                  <button
                    onClick={() => setDeleteId(item.id)}
                    className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-red-100 transition-colors"
                    title="Löschen"
                    data-testid={`delete-media-${item.id}`}
                  >
                    <Trash2 size={14} className="text-red-600" />
                  </button>
                </div>

                {/* Info */}
                <div className="text-white">
                  <p className="text-sm truncate">{item.original_name}</p>
                  <p className="text-xs text-white/70">{formatSize(item.size)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {media.length > 0 && (
        <div className="mt-8 pt-8 border-t border-[#E5E5E5]">
          <p className="text-sm text-[#666666]">
            {media.length} Dateien • {formatSize(media.reduce((sum, m) => sum + m.size, 0))} gesamt
          </p>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bild löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie dieses Bild löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden und kann Fahrzeuganzeigen beschädigen, die dieses Bild verwenden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
